import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useJobs from '../hooks/useJobs';
import useOrgRole from '../hooks/useOrgRole';
import DataTable from '../components/utility/dataTable';
import { formatDate, formatInstant, formatCurrency } from '../utils/formatters';

// Worker-hidden columns. Money figures are Manager+ only — the API's
// GetMyAssignments doesn't return them and the role-aware <DataTable>
// must not surface them in the column-picker either.
const MONEY_KEYS = new Set(['quotedRevenue', 'estimatedCost', 'actualCost', 'actualRevenue']);

const JOB_STATUS_COLORS = {
    Open:          { bg: '#dbeafe', text: '#2563eb' },
    'In Progress': { bg: '#d1fae5', text: '#059669' },
    'On Hold':     { bg: '#fef3c7', text: '#d97706' },
    Completed:     { bg: '#e5e7eb', text: '#374151' },
    Cancelled:     { bg: '#fee2e2', text: '#dc2626' },
};

// `key` is the JSON property name returned by the API (used for cell render
// and default backend sort/search). `path` is the dotted property path the
// backend resolves via reflection — needed when the column reads through a
// navigation property (e.g. `customer.name` off the Customer nav).
//
// Money cells take (value, row) and format with the row's own currency, so
// a page of mixed-currency jobs renders each row in its own symbol.
const money = (v, row) => formatCurrency(v, row?.currency);
const statusChip = (v) => {
    const sc = JOB_STATUS_COLORS[v] || JOB_STATUS_COLORS.Open;
    return (
        <span style={{ backgroundColor: sc.bg, color: sc.text, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {v}
        </span>
    );
};
const ALL_COLUMNS = [
    { key: 'jobNumber',     label: 'Job #',     system: true },
    { key: 'title',         label: 'Title',     system: false },
    { key: 'customerName',  path: 'customer.name', label: 'Customer',  system: false },
    { key: 'status',        label: 'Status',    system: false, render: statusChip },
    { key: 'quotedRevenue', label: 'Quoted',    system: false, render: money },
    { key: 'estimatedCost', label: 'Est. Cost', system: false, render: money },
    { key: 'actualCost',    label: 'Cost',      system: false, render: money },
    { key: 'actualRevenue', label: 'Revenue',   system: false, render: money },
    { key: 'startDate',     label: 'Start',     system: false, render: formatDate },
    { key: 'endDate',       label: 'End',       system: false, render: formatDate },
    { key: 'createdAt',     label: 'Created',   system: false, render: formatInstant },
];

const DEFAULT_VISIBLE = {
    jobNumber: true,
    title: true,
    customerName: true,
    status: true,
    quotedRevenue: true,
    actualCost: true,
    startDate: true,
    estimatedCost: false,
    actualRevenue: false,
    endDate: false,
    createdAt: false,
};

const TABLE_NAME = 'marginpal-jobs';

// Backend doesn't enforce these — Job.Status is a free string. `Open` matches
// Job.Create()'s default. Promote to a backend enum when CRUD lands and we
// need to validate writes.
const STATUS_OPTIONS = [
    { value: null,           label: 'All statuses' },
    { value: 'Open',         label: 'Open' },
    { value: 'In Progress',  label: 'In Progress' },
    { value: 'On Hold',      label: 'On Hold' },
    { value: 'Completed',    label: 'Completed' },
    { value: 'Cancelled',    label: 'Cancelled' },
];

function loadVisibleColumns() {
    try {
        const stored = localStorage.getItem(TABLE_NAME);
        // Merge so newly-added columns pick up their default visibility
        // instead of silently being undefined (= hidden) for existing users.
        if (stored) return { ...DEFAULT_VISIBLE, ...JSON.parse(stored) };
    } catch { /* fall through to default */ }
    return DEFAULT_VISIBLE;
}

// Default set: every non-system column that's visible by default. Mirrors
// the derivation inside DataTable so "Reset to Defaults" and the initial
// filter seed agree on the same set.
const DEFAULT_SEARCH_FIELDS = ALL_COLUMNS
    .filter(col => !col.system && DEFAULT_VISIBLE[col.key])
    .map(col => col.path ?? col.key);

function loadSearchableFields() {
    try {
        const stored = localStorage.getItem(`${TABLE_NAME}_search`);
        if (stored) return JSON.parse(stored);
    } catch { /* fall through to default */ }
    return DEFAULT_SEARCH_FIELDS;
}

const DEFAULT_FILTER = {
    pageNumber: 1,
    pageSize: 20,
    sortOptions: { key: 'createdAt', direction: 'descending' },
    // Seeded in useState initializer below so the first query already has
    // the persisted searchFields — previously DataTable pushed them up on
    // mount, which fired a wasted initial request with empty fields.
    searchOptions: { searchTerm: '', searchFields: [] },
    statusFilter: null,
};

export default function JobsPage() {
    const navigate = useNavigate();
    const role = useOrgRole();
    const isWorker = role === 'Worker';

    const [filter, setFilter] = useState(() => ({
        ...DEFAULT_FILTER,
        searchOptions: { searchTerm: '', searchFields: loadSearchableFields() },
    }));
    const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);

    const { data, isLoading, error } = useJobs(filter);

    // Strip money columns out of every projection (column list, defaults,
    // settings picker, search fields) when the viewer is a Worker. We do
    // it in one place so the picker can never re-toggle a hidden column
    // back on, and so the table doesn't render an empty cell for a key
    // GetMyAssignments doesn't return.
    const availableColumns       = useMemo(() => isWorker ? ALL_COLUMNS.filter(c => !MONEY_KEYS.has(c.key)) : ALL_COLUMNS, [isWorker]);
    const availableDefaultVisible = useMemo(() => {
        if (!isWorker) return DEFAULT_VISIBLE;
        const out = {};
        for (const k of Object.keys(DEFAULT_VISIBLE)) if (!MONEY_KEYS.has(k)) out[k] = DEFAULT_VISIBLE[k];
        return out;
    }, [isWorker]);

    const columns = availableColumns.filter(col => visibleColumns[col.key]);

    const filters = [
        {
            id: 'status',
            type: 'select',
            label: 'Status',
            placeholder: 'All statuses',
            options: STATUS_OPTIONS,
            value: filter.statusFilter,
            isClearable: true,
            // Inline beside the search box, not in the advanced drawer.
            advanced: false,
        },
    ];

    const handleFilterChange = ({ filterId, value }) => {
        setFilter(prev => {
            switch (filterId) {
                case 'pageNumber':
                    return { ...prev, pageNumber: Number(value) };
                case 'pageSize':
                    return { ...prev, pageSize: Number(value), pageNumber: 1 };
                case 'sortOptions':
                    return { ...prev, sortOptions: value, pageNumber: 1 };
                case 'search':
                    return {
                        ...prev,
                        pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchTerm: value },
                    };
                case 'searchFields':
                    return {
                        ...prev,
                        pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchFields: value },
                    };
                case 'status':
                    return { ...prev, statusFilter: value, pageNumber: 1 };
                default:
                    return prev;
            }
        });
    };

    return (
        <div>
            <h1 className="fw-bold mb-1">Jobs</h1>
            <p className="text-muted mb-4">Track and manage your jobs.</p>
            <DataTable
                tableName={TABLE_NAME}
                columns={columns}
                data={data?.items ?? []}
                isLoading={isLoading}
                error={error}
                totalRecords={data?.totalRecords ?? 0}
                filter={filter}
                filters={filters}
                onRowClick={(e, row) => navigate(`/jobs/${row.id}`)}
                actions={[]}
                showSettings
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={availableDefaultVisible}
                settingsColumns={availableColumns}
                onFilterChange={handleFilterChange}
                sortConfiguration={{ key: 'createdAt', direction: 'descending' }}
            />
        </div>
    );
}
